"use client"

import { useRef, useState } from "react"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import { PauseIcon, PlayIcon } from "lucide-react"

import { isHeroVideo, MAX_HERO_INTERVAL_MS } from "@workspace/model"
import { cn } from "@workspace/ui/lib/utils"

import type { ShowcaseSlide } from "@/components/home/showcase/showcase-slides"
import { DARK_BAND_STYLE } from "@/components/showcase/band-tokens"
import { Link } from "@/i18n/navigation"

gsap.registerPlugin(useGSAP)

/** One caption-strip message, optionally with a pill link. */
export interface ShowcaseCaption {
  text: string
  cta?: { label: string; href: string }
}

/** Longest tile transition, in seconds; short intervals scale it down. */
const MAX_TRANSITION_S = 1.4
/** A video slide advances when its clip ends, or after this at the latest. */
const MAX_VIDEO_HOLD_S = MAX_HERO_INTERVAL_MS / 1000

/**
 * The tile transitions, played in order (the reference banner runs its jssor
 * set with `$TransitionsOrder: 1`). Each slices the INCOMING photo into a
 * `cols × rows` grid of tiles over the outgoing one and tweens them home.
 *
 * `from` offsets are in tile units (`xPercent: 100` = one tile width), `dir`
 * mirrors horizontal motion for RTL, and `origin` is the stagger's grid
 * origin as `[x, y]` ratios — flipped for RTL too, so the wave always starts
 * at the reading start.
 */
interface TileEffect {
  cols: number
  rows: number
  from: (dir: 1 | -1) => gsap.TweenVars
  origin: (dir: 1 | -1) => gsap.StaggerVars["from"]
  ease: string
}

const TILE_EFFECTS: readonly TileEffect[] = [
  // Stairs: an 8×4 grid flies in from up-and-behind, growing from nothing,
  // in a diagonal wave from the top reading-start corner.
  {
    cols: 8,
    rows: 4,
    from: (dir) => ({
      xPercent: 160 * dir,
      yPercent: -40,
      scale: 0,
      autoAlpha: 0,
    }),
    origin: (dir) => (dir === 1 ? [0, 0] : [1, 0]),
    ease: "power3.out",
  },
  // Chess: twelve columns drop in, alternating from above and below.
  {
    cols: 12,
    rows: 1,
    from: () => ({
      yPercent: (index: number) => (index % 2 === 0 ? -100 : 100),
    }),
    origin: (dir) => (dir === 1 ? "start" : "end"),
    ease: "power2.inOut",
  },
  // Columns: twelve strips slide in from the reading end, fading up.
  {
    cols: 12,
    rows: 1,
    from: (dir) => ({ xPercent: 240 * dir, autoAlpha: 0 }),
    origin: (dir) => (dir === 1 ? "start" : "end"),
    ease: "expo.out",
  },
  // Dissolve: an 8×4 grid pops in in random order.
  {
    cols: 8,
    rows: 4,
    from: () => ({ scale: 0.2, autoAlpha: 0 }),
    origin: () => "random",
    ease: "back.out(1.6)",
  },
]

/**
 * Builds the tile grid for `effect` inside `layer`, each tile cropping its own
 * window of `src`, and tweens it in. Every tile holds a full-banner-sized image
 * offset by its own position, so the tiles' `object-fit: cover` crop is
 * pixel-identical to the base slide's and the hand-off at the end is seamless.
 */
function playTiles(
  layer: HTMLElement,
  src: string,
  effect: TileEffect,
  dir: 1 | -1,
  duration: number,
  onComplete: () => void
): gsap.core.Tween {
  const { cols, rows } = effect
  const tiles: HTMLElement[] = []

  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const tile = document.createElement("div")
      // +1px on the box hides sub-pixel seams between neighbouring tiles.
      tile.style.cssText = `position:absolute;overflow:hidden;left:${(col * 100) / cols}%;top:${(row * 100) / rows}%;width:calc(${100 / cols}% + 1px);height:calc(${100 / rows}% + 1px);`
      const image = document.createElement("img")
      image.src = src
      image.alt = ""
      image.decoding = "async"
      image.style.cssText = `position:absolute;max-width:none;left:${-col * 100}%;top:${-row * 100}%;width:${cols * 100}%;height:${rows * 100}%;object-fit:cover;`
      tile.append(image)
      tiles.push(tile)
    }
  }
  layer.replaceChildren(...tiles)

  return gsap.fromTo(tiles, effect.from(dir), {
    xPercent: 0,
    yPercent: 0,
    scale: 1,
    autoAlpha: 1,
    duration: duration * 0.55,
    ease: effect.ease,
    stagger: {
      grid: [rows, cols],
      from: effect.origin(dir),
      amount: duration * 0.45,
    },
    onComplete,
  })
}

/**
 * The client half of the `showcase` banner (see `./showcase-banner`): base
 * photos/videos stacked under a tile layer, a solid near-black caption strip
 * pinned to the top edge, and dot/pause controls. Square-cornered, flush
 * under the nav band, as on the reference site.
 *
 * Server-rendered with the first photo and caption showing, so the banner is
 * complete without JS and under `prefers-reduced-motion` (where dots swap
 * photos instantly and nothing autoplays). Motion lives under
 * `gsap.matchMedia`. Autoplay pauses while the banner is hovered or holds
 * focus, is off screen, the tab is hidden, or the diner pressed pause (WCAG
 * 2.2.2); the caption strip is a polite live region only while NOT
 * autoplaying, so a screen reader isn't talked over every few seconds.
 *
 * Videos (muted, inline, never `autoplay`) show their poster until JS plays
 * them, and stay on it under reduced motion or Save-Data. Only the active
 * video plays, and only while on screen, the tab is visible and the diner
 * hasn't pressed pause. Transitions INTO a video slice its poster (crossfade
 * when it has none); the clip starts once the transition lands, holds until it
 * ends (capped at 30s) and loops while autoplay is held or off.
 *
 * Plain `<img>` — tenant photos come from arbitrary CDNs not registered with
 * next/image, same as the hero slideshow.
 */
export function ShowcaseBannerSlider({
  slides,
  captions,
  intervalMs,
  autoplay,
  fallbackTitle,
  rounded = false,
  labels,
}: {
  slides: ShowcaseSlide[]
  captions: ShowcaseCaption[]
  intervalMs: number
  /** False when the tenant set `heroAnimation.effect: "none"`. */
  autoplay: boolean
  /** Headline for the photo-less primary panel. */
  fallbackTitle: string
  /** Rounded corners instead of the default square ones. */
  rounded?: boolean
  labels: {
    pause: string
    play: string
    /** Contains a literal `{index}` placeholder (1-based). */
    goTo: string
    slides: string
  }
}) {
  const rootRef = useRef<HTMLDivElement>(null)
  const tilesRef = useRef<HTMLDivElement>(null)
  const captionRef = useRef<HTMLDivElement>(null)

  const [active, setActive] = useState(0)
  const [paused, setPaused] = useState(false)
  const currentRef = useRef(0)
  const pausedRef = useRef(false)
  const holdRef = useRef(false)
  // Swapped by the matchMedia branches: instant under reduced motion, tiles
  // otherwise. `syncRef` re-evaluates whether the autoplay clock may run.
  const goRef = useRef<(to: number) => void>(() => {})
  const syncRef = useRef<() => void>(() => {})

  const cycles = slides.length > 1
  const autoplays = cycles && autoplay
  const hasVideo = slides.some(isHeroVideo)

  useGSAP(
    () => {
      const root = rootRef.current
      const layer = tilesRef.current
      if (!(cycles || hasVideo) || !root || !layer) return
      const bases = gsap.utils.toArray<HTMLElement>(
        "[data-showcase-slide]",
        root
      )

      const show = (to: number) => {
        bases.forEach((base, index) => {
          gsap.set(base, { autoAlpha: index === to ? 1 : 0, zIndex: "auto" })
        })
      }

      const mm = gsap.matchMedia()

      mm.add("(prefers-reduced-motion: reduce)", () => {
        goRef.current = (to) => {
          currentRef.current = to
          show(to)
          setActive(to)
        }
        syncRef.current = () => {}
      })

      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const hold = intervalMs / 1000
        const duration = Math.min(MAX_TRANSITION_S, hold * 0.6)
        const dir: 1 | -1 = getComputedStyle(root).direction === "rtl" ? -1 : 1
        const saveData =
          (navigator as Navigator & { connection?: { saveData?: boolean } })
            .connection?.saveData === true
        const videoAt = (index: number) => {
          const base = bases[index]
          return !saveData && base instanceof HTMLVideoElement
            ? base
            : undefined
        }

        let effectIndex = 0
        let inView = true
        let pending: gsap.core.Tween | undefined
        let running: gsap.core.Tween | undefined
        // Detaches the active video's `ended` listener on every change.
        let listeners = new AbortController()

        const canPlay = () => inView && !document.hidden && !pausedRef.current

        const canRun = () => autoplays && canPlay() && !holdRef.current

        // Plays the active video when allowed; every other one stays paused.
        const syncVideo = () => {
          bases.forEach((base, index) => {
            if (
              base instanceof HTMLVideoElement &&
              index !== currentRef.current
            )
              base.pause()
          })
          const video = videoAt(currentRef.current)
          if (!video) return
          if (!canPlay() || running) {
            video.pause()
            return
          }
          // React doesn't reliably reflect `muted` onto the element, and
          // browsers refuse to autoplay unmuted video.
          video.muted = true
          video.play().catch((error: unknown) => {
            // AbortError = our own pause() interrupted it — not a refusal.
            if (error instanceof DOMException && error.name === "AbortError")
              return
            // Autoplay refused (e.g. power saving): stay on the poster and
            // hold like a photo so the banner doesn't stall.
            if (video !== videoAt(currentRef.current) || !autoplays) return
            pending?.kill()
            pending = gsap.delayedCall(hold, next)
            // Not `sync()` — that would retry play() and loop on refusal.
            pending.paused(!canRun())
          })
        }

        const sync = () => {
          pending?.paused(!canRun())
          syncVideo()
        }

        function next() {
          go((currentRef.current + 1) % bases.length)
        }

        const schedule = () => {
          pending?.kill()
          listeners.abort()
          listeners = new AbortController()
          const video = videoAt(currentRef.current)
          if (video) {
            video.currentTime = 0
            // Loops while autoplay is off; otherwise its end advances the
            // banner — or, while held, replays it.
            video.loop = !autoplays
            video.addEventListener(
              "ended",
              () => {
                if (canRun()) next()
                else {
                  video.currentTime = 0
                  syncVideo()
                }
              },
              { signal: listeners.signal }
            )
          }
          if (autoplays) {
            pending = gsap.delayedCall(video ? MAX_VIDEO_HOLD_S : hold, next)
            // Warm up the next clip while this slide is on screen.
            const upcoming = videoAt((currentRef.current + 1) % bases.length)
            if (upcoming) upcoming.preload = "auto"
          }
          sync()
        }

        function go(to: number) {
          // Land any in-flight transition first, so its hand-off runs.
          running?.progress(1)
          const from = currentRef.current
          if (to === from) return
          pending?.kill()
          listeners.abort()
          currentRef.current = to
          setActive(to)

          const land = () => {
            show(to)
            layer!.replaceChildren()
            running = undefined
            schedule()
          }
          const slide = slides[to]!
          // Tiles slice a still: the photo, or a video's poster.
          const still = isHeroVideo(slide) ? slide.posterUrl : slide.url
          if (still) {
            const effect = TILE_EFFECTS[effectIndex++ % TILE_EFFECTS.length]!
            running = playTiles(layer!, still, effect, dir, duration, land)
          } else {
            // A poster-less video has no still to slice: fade it in over the
            // outgoing slide instead.
            running = gsap.fromTo(
              bases[to]!,
              { autoAlpha: 0, zIndex: 1 },
              { autoAlpha: 1, duration, ease: "power1.inOut", onComplete: land }
            )
          }
        }

        goRef.current = go
        syncRef.current = sync

        const observer = new IntersectionObserver(([entry]) => {
          inView = entry?.isIntersecting ?? true
          sync()
        })
        observer.observe(root)
        document.addEventListener("visibilitychange", sync)
        schedule()

        // Tweens started by later `go` calls aren't recorded by this context,
        // so finish them by hand — leaving the CURRENT slide showing (the dots
        // and caption already point at it) rather than snapping back to the
        // first one. Videos fall back to paused.
        return () => {
          observer.disconnect()
          document.removeEventListener("visibilitychange", sync)
          listeners.abort()
          pending?.kill()
          running?.progress(1)
          // Landing a transition re-schedules; undo that too.
          pending?.kill()
          listeners.abort()
          gsap.killTweensOf(layer.children)
          layer.replaceChildren()
          show(currentRef.current)
          for (const base of bases) {
            if (base instanceof HTMLVideoElement) base.pause()
          }
        }
      })
    },
    {
      scope: rootRef,
      dependencies: [
        cycles,
        autoplays,
        hasVideo,
        intervalMs,
        slides
          .map((slide) => `${slide.type ?? "image"}:${slide.url}`)
          .join("\n"),
      ],
      revertOnUpdate: true,
    }
  )

  const captionIndex = captions.length > 0 ? active % captions.length : -1

  // Caption change: the new line drops into the strip from above. Compared
  // against the last index (not a first-run flag) so a StrictMode re-run of
  // the mount effect doesn't animate the server-rendered caption.
  const shownCaption = useRef(captionIndex)
  useGSAP(
    () => {
      if (shownCaption.current === captionIndex) return
      shownCaption.current = captionIndex
      gsap.matchMedia().add("(prefers-reduced-motion: no-preference)", () => {
        gsap.fromTo(
          "[data-caption-active]",
          { yPercent: -100, autoAlpha: 0 },
          { yPercent: 0, autoAlpha: 1, duration: 0.5, ease: "power2.out" }
        )
      })
    },
    { scope: captionRef, dependencies: [captionIndex] }
  )

  const togglePaused = () => {
    pausedRef.current = !pausedRef.current
    setPaused(pausedRef.current)
    syncRef.current()
  }

  const setHold = (value: boolean) => {
    holdRef.current = value
    syncRef.current()
  }

  const caption = captionIndex >= 0 ? captions[captionIndex] : undefined
  const liveCaption = !autoplays || paused

  // Sized by its CONTAINER, not the viewport: the banner is full-width on the
  // home page but also renders in narrow columns (the dev gallery), and the
  // caption strip must fit either. Steps are in px — at the home page's 920px
  // it must land on the reference's 920×307 (3:1) banner with a 51px strip,
  // whatever the root font size makes a rem — and all of one kind, so they
  // cascade in width order.
  return (
    <div className="@container max-md:h-full">
      <div
        ref={rootRef}
        aria-roledescription="carousel"
        onPointerEnter={() => setHold(true)}
        onPointerLeave={() => setHold(false)}
        onFocus={() => setHold(true)}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setHold(false)
        }}
        className={cn(
          "relative isolate aspect-[4/3] w-full overflow-hidden bg-primary text-primary-foreground @min-[576px]:aspect-[2/1] @min-[880px]:aspect-[3/1]",
          // Phones: the whole first screen, square-cornered (the home stage
          // lays the welcome blocks over it).
          "max-md:aspect-auto! max-md:h-full",
          rounded && "md:rounded-[calc(var(--radius)*2)]"
        )}
      >
        {slides.length === 0 ? (
          // No photos anywhere: a flat tenant-primary panel still carries the
          // caption strip, so the banner keeps its job.
          <div
            // Phones lay the tagline over the panel instead.
            className="absolute inset-0 grid place-items-center px-6 pt-12 max-md:hidden"
            style={{
              backgroundImage:
                "linear-gradient(135deg, var(--primary), color-mix(in oklab, var(--primary), black 30%))",
            }}
          >
            <p className="text-center font-display text-4xl tracking-wide text-balance uppercase @min-[576px]:text-6xl">
              {fallbackTitle}
            </p>
          </div>
        ) : (
          slides.map((slide, index) => {
            const hidden =
              index === 0
                ? undefined
                : { opacity: 0, visibility: "hidden" as const }
            return isHeroVideo(slide) ? (
              <video
                key={slide.url}
                data-showcase-slide
                src={slide.url}
                poster={slide.posterUrl}
                muted
                playsInline
                loop={!autoplays}
                // With a poster nothing needs fetching until JS plays the clip;
                // without one, fetch metadata so the first frame can show.
                preload={slide.posterUrl ? "none" : "metadata"}
                aria-label={slide.alt || undefined}
                className="absolute inset-0 size-full object-cover"
                style={hidden}
              />
            ) : (
              <img
                key={slide.url}
                data-showcase-slide
                src={slide.url}
                alt={slide.alt ?? ""}
                // The first photo is the LCP candidate; the rest load behind it
                // so the tiles never slice a photo that isn't there yet.
                fetchPriority={index === 0 ? "high" : "low"}
                decoding="async"
                className="absolute inset-0 size-full object-cover"
                style={hidden}
              />
            )
          })
        )}

        <div
          ref={tilesRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[1]"
        />

        {caption ? (
          <div
            ref={captionRef}
            data-slot="banner-caption"
            style={DARK_BAND_STYLE}
            aria-live={liveCaption ? "polite" : "off"}
            // A tenant-primary strip on phones, as on the reference's
            // mobile template; near-black from `md`.
            className="absolute inset-x-0 top-0 z-[2] overflow-hidden bg-background text-foreground max-md:bg-primary max-md:text-primary-foreground"
          >
            <p
              key={captionIndex}
              data-caption-active
              className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-3 py-1.5 text-center font-display text-[17px] leading-[22px] uppercase @min-[576px]:gap-x-3 @min-[576px]:px-4 @min-[576px]:py-2 @min-[576px]:text-lg @min-[880px]:min-h-[51px] @min-[880px]:py-0 @min-[880px]:text-[18pt] @min-[880px]:leading-[33px]"
            >
              {/* Staff lines run to 250 characters: clamp so a long one
                  never buries the photo. Full text stays in the title. */}
              <span
                title={caption.text}
                className="line-clamp-3 max-w-full break-words @min-[880px]:line-clamp-2"
              >
                {caption.text}
              </span>
              {caption.cta ? (
                <Link
                  href={caption.cta.href}
                  // A plain "More…" link on the phones' primary strip.
                  className="rounded-[15px] bg-primary px-2.5 py-0.5 text-primary-foreground hover:bg-primary/90 max-md:px-1 max-md:underline-offset-4 max-md:hover:underline"
                >
                  {caption.cta.label}
                </Link>
              ) : null}
            </p>
          </div>
        ) : null}

        {cycles || hasVideo ? (
          <div className="absolute inset-x-0 bottom-0 z-[2] flex items-center justify-center gap-3 p-3">
            {cycles ? (
              <div
                role="group"
                aria-label={labels.slides}
                className="flex items-center gap-1.5 rounded-4xl bg-black/45 px-2.5 py-1.5 backdrop-blur-sm"
              >
                {slides.map((slide, index) => (
                  <button
                    key={slide.url}
                    type="button"
                    aria-label={labels.goTo.replace(
                      "{index}",
                      String(index + 1)
                    )}
                    aria-current={index === active || undefined}
                    onClick={() => goRef.current(index)}
                    className={cn(
                      "size-2.5 rounded-full transition-colors",
                      index === active
                        ? "bg-white"
                        : "bg-white/45 hover:bg-white/75"
                    )}
                  />
                ))}
              </div>
            ) : null}
            {/* Pause stops both the autoplay clock and any playing video
                (WCAG 2.2.2), so a lone video slide gets the button too. */}
            {autoplays || hasVideo ? (
              <button
                type="button"
                onClick={togglePaused}
                aria-label={paused ? labels.play : labels.pause}
                className="grid size-7 place-items-center rounded-full bg-black/45 text-white backdrop-blur-sm hover:bg-black/65"
              >
                {paused ? (
                  <PlayIcon className="size-3.5" aria-hidden />
                ) : (
                  <PauseIcon className="size-3.5" aria-hidden />
                )}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}
